import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { articlesApi, voiceProfileApi, authApi, userSettingsApi } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Link2, Lightbulb, ArrowRight, Loader2, Mic, ChevronDown, Settings, Check, Lock, Globe2, Share2, Image as ImageIcon, Palette } from "lucide-react";

const LANGUAGES = [
  { id: "es", label: "Espanol", flag: "ES" },
  { id: "en", label: "English", flag: "EN" },
  { id: "pt", label: "Portugues", flag: "PT" },
  { id: "fr", label: "Francais", flag: "FR" },
  { id: "de", label: "Deutsch", flag: "DE" },
  { id: "it", label: "Italiano", flag: "IT" },
];

const NETWORKS = [
  { id: "linkedin", label: "LinkedIn", color: "#0A66C2" },
  { id: "facebook", label: "Facebook", color: "#1877F2" },
  { id: "instagram", label: "Instagram", color: "#E4405F" },
  { id: "x", label: "X (Twitter)", color: "#000000" },
];

const STYLES = [
  { id: "fotorrealista", label: "Fotorrealista" },
  { id: "3d", label: "3D" },
  { id: "ilustracion", label: "Ilustracion" },
  { id: "minimalista", label: "Minimalista" },
  { id: "corporativo", label: "Corporativo" },
  { id: "artistico", label: "Artistico" },
  { id: "geometrico", label: "Geometrico" },
  { id: "infografico", label: "Infografico" },
  { id: "editorial", label: "Editorial" },
  { id: "cinematografico", label: "Cinematografico" },
  { id: "comic", label: "Comic" },
  { id: "pixar", label: "Pixar / 3D expresivo" },
  { id: "manga", label: "Manga" },
  { id: "acuarela", label: "Acuarela" },
  { id: "vintage", label: "Vintage / Retro" },
  { id: "street_art", label: "Street Art" },
  { id: "scifi", label: "Sci-Fi / Futurista" },
  { id: "flat_design", label: "Flat Design" },
  { id: "organico", label: "Organico / Natural" },
  { id: "dark_dramatico", label: "Dark / Dramatico" },
];

const MOODS = [
  { id: "luminoso", label: "Luminoso" },
  { id: "oscuro", label: "Oscuro" },
  { id: "neutro", label: "Neutro" },
  { id: "calido", label: "Calido" },
  { id: "frio", label: "Frio" },
];

const ENGINES_NEW = [
  { id: "openai", label: "GPT Image 1", desc: "Fotorrealista y detallado" },
  { id: "nano_banana", label: "Nano Banana", desc: "Creativo y artistico" },
];

const FORMALIDAD = ["formal", "semiformal", "informal"];
const CERCANIA = ["distante", "neutro", "cercano", "intimo"];
const ENERGIA = ["reflexivo", "equilibrado", "dinamico", "intenso"];
const ANGULOS = ["Problema → Solución", "Caso real", "Datos y tendencia", "Contraintuitivo", "Paso a paso", "Comparativa", "Opinión experta", "Futuro / Prospectiva"];
const OBJETIVOS = ["Informar", "Didáctico", "Práctico / Aplicable", "Generar autoridad", "Controversial", "Captar leads", "Fidelizar comunidad", "Viralizable"];

// ─── Locked Feature Overlay ───

function LockedOverlay({ planName }) {
  const navigate = useNavigate();
  return (
    <div data-testid="locked-overlay" className="absolute inset-0 z-20 rounded-xl flex items-center justify-center"
      style={{ background: "rgba(11,22,34,0.35)" }}>
      <div className="text-center px-5 py-3 rounded-xl" style={{ background: "rgba(11,22,34,0.9)", border: "1px solid rgba(251,191,36,0.3)" }}>
        <Lock className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#FBBF24" }} />
        <p className="text-sm font-semibold mb-1.5" style={{ color: "#FBBF24" }}>Disponible en plan {planName}</p>
        <button onClick={() => navigate("/pricing")}
          className="text-xs px-5 py-2 rounded-full transition-all hover:opacity-90"
          style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0B1622", fontWeight: 600 }}>
          Mejorar plan
        </button>
      </div>
    </div>
  );
}

// ─── Voice Profile Summary (with tier check) ───

function VoiceProfileSummary({ hasAccess }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editProfile, setEditProfile] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      voiceProfileApi.get().then((res) => {
        setProfile(res.data);
        setEditProfile(res.data);
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await voiceProfileApi.update(editProfile);
      setProfile(editProfile);
      toast.success("Perfil actualizado");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  const hasProfile = profile?.formalidad;

  return (
    <div data-testid="voice-profile-summary" className="relative rounded-xl border overflow-hidden mb-5"
      style={{ background: "#0F2035", borderColor: hasAccess ? "rgba(0,212,255,0.15)" : "rgba(251,191,36,0.15)" }}>
      {!hasAccess && <LockedOverlay planName="Pro" />}
      <button onClick={() => hasAccess && setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left"
        disabled={!hasAccess}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: hasProfile ? "rgba(0,212,255,0.1)" : "rgba(249,115,22,0.1)" }}>
            <Mic className="w-4 h-4" style={{ color: hasProfile ? "#00D4FF" : "#F97316" }} />
          </div>
          <div>
            <div className="text-sm font-medium" style={{ color: "#E2E8F0", fontFamily: "'Outfit'" }}>
              {hasProfile ? "Perfil de Voz Activo" : "Perfil de Voz"}
            </div>
            {hasProfile && hasAccess && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {profile.formalidad && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,212,255,0.06)", color: "#00D4FF" }}>{profile.formalidad}</span>}
                {profile.cercania && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,212,255,0.06)", color: "#00D4FF" }}>{profile.cercania}</span>}
                {profile.energia && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,212,255,0.06)", color: "#00D4FF" }}>{profile.energia}</span>}
                {profile.angulo_editorial && <span className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(249,115,22,0.06)", color: "#F97316" }}>{profile.angulo_editorial}</span>}
              </div>
            )}
          </div>
        </div>
        {hasAccess && (
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} style={{ color: "#94A3B8" }} />
        )}
      </button>

      {expanded && hasAccess && editProfile && (
        <div className="px-4 pb-4 space-y-3 animate-fade-in-up" style={{ borderTop: "1px solid #1E3A5F" }}>
          <div className="pt-3">
            <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Formalidad</Label>
            <div className="flex flex-wrap gap-1.5">
              {FORMALIDAD.map((v) => (
                <button key={v} onClick={() => setEditProfile({ ...editProfile, formalidad: v })}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all"
                  style={{ background: editProfile.formalidad === v ? "rgba(0,212,255,0.1)" : "transparent", borderColor: editProfile.formalidad === v ? "rgba(0,212,255,0.4)" : "#1E3A5F", color: editProfile.formalidad === v ? "#00D4FF" : "#94A3B8" }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Cercania</Label>
            <div className="flex flex-wrap gap-1.5">
              {CERCANIA.map((v) => (
                <button key={v} onClick={() => setEditProfile({ ...editProfile, cercania: v })}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all"
                  style={{ background: editProfile.cercania === v ? "rgba(0,212,255,0.1)" : "transparent", borderColor: editProfile.cercania === v ? "rgba(0,212,255,0.4)" : "#1E3A5F", color: editProfile.cercania === v ? "#00D4FF" : "#94A3B8" }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Energia</Label>
            <div className="flex flex-wrap gap-1.5">
              {ENERGIA.map((v) => (
                <button key={v} onClick={() => setEditProfile({ ...editProfile, energia: v })}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all"
                  style={{ background: editProfile.energia === v ? "rgba(0,212,255,0.1)" : "transparent", borderColor: editProfile.energia === v ? "rgba(0,212,255,0.4)" : "#1E3A5F", color: editProfile.energia === v ? "#00D4FF" : "#94A3B8" }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <Button data-testid="save-voice-quick-btn" size="sm" onClick={handleSave} disabled={saving}
              className="rounded-full text-xs h-8 px-4"
              style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              Guardar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Locked Angle/Objective Section ───

function AngleObjectiveSection({ hasAccess, profile }) {
  if (!hasAccess) {
    return (
      <div className="relative rounded-xl border overflow-hidden mb-5"
        style={{ background: "#0F2035", borderColor: "rgba(251,191,36,0.15)" }}>
        <LockedOverlay planName="Pro" />
        <div className="p-4 opacity-40">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" style={{ color: "#F97316" }} />
            <span className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Angulo Editorial y Objetivo</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ANGULOS.slice(0, 4).map((a) => (
              <span key={a} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: "#1E3A5F", color: "#64748B" }}>{a}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border p-4 mb-5" style={{ background: "#0F2035", borderColor: "rgba(0,212,255,0.15)" }}>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4" style={{ color: "#F97316" }} />
        <span className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Angulo y Objetivo</span>
      </div>
      {profile?.angulo_editorial && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <span className="text-xs px-2.5 py-0.5 rounded-full" style={{ background: "rgba(0,212,255,0.06)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.2)" }}>
            {profile.angulo_editorial}
          </span>
          {profile?.objetivo_contenido && (
            <span className="text-xs px-2.5 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.06)", color: "#F97316", border: "1px solid rgba(249,115,22,0.2)" }}>
              {profile.objetivo_contenido}
            </span>
          )}
        </div>
      )}
      <p className="text-xs mt-2" style={{ color: "#475569" }}>
        Configurable en <a href="/settings" style={{ color: "#00D4FF" }}>Ajustes → Angulo Editorial</a>
      </p>
    </div>
  );
}

// ─── Main Component ───

export default function NewArticle() {
  const [inputType, setInputType] = useState("idea");
  const [content, setContent] = useState("");
  const [topicHint, setTopicHint] = useState("");
  const [language, setLanguage] = useState("es");
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [autoImage, setAutoImage] = useState(true);
  const [imgOverrides, setImgOverrides] = useState({
    engine: "openai",
    style: "fotorrealista",
    secondary_style: "",
    mood: "",
    custom_prompt: "",
  });
  const [imgSectionOpen, setImgSectionOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [tierAccess, setTierAccess] = useState(null);
  const [voiceProfile, setVoiceProfile] = useState(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    authApi.tierAccess().then((res) => setTierAccess(res.data)).catch(() => {});
    voiceProfileApi.get().then((res) => setVoiceProfile(res.data)).catch(() => {});
    // Pre-fill image overrides from user profile defaults
    userSettingsApi.get().then((res) => {
      const vp = res.data?.visual_preferences || {};
      setImgOverrides((prev) => ({
        ...prev,
        engine: vp.default_engine || prev.engine,
        style: vp.default_style || prev.style,
        secondary_style: vp.secondary_style || "",
        mood: vp.mood || "",
        custom_prompt: "",
      }));
    }).catch(() => {});
  }, []);

  const hasProAccess = tierAccess?.has_voice_profile ?? false;

  const pollStatus = useCallback((jobId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await articlesApi.generateStatus(jobId);
        if (res.data.status === "completed") {
          clearInterval(pollRef.current);
          setLoading(false);
          toast.success("Artículo forjado correctamente");
          navigate(`/articles/${res.data.article_id}`);
        } else if (res.data.status === "error") {
          clearInterval(pollRef.current);
          setLoading(false); setStatusMsg("");
          toast.error(res.data.detail || "Error al generar");
        } else {
          const pct = Math.min(95, attempts * 3.5);
          setProgress(pct);
          setStatusMsg(attempts < 6 ? "Analizando tu idea..." : attempts < 12 ? "Claude esta redactando el articulo..." : attempts < 20 ? "Generando FAQ y optimizando SEO..." : attempts < 30 ? "Revisando calidad y estructura..." : "Finalizando, casi listo...");
        }
      } catch {
        if (attempts >= 60) { clearInterval(pollRef.current); setLoading(false); toast.error("Timeout"); }
      }
    }, 5000);
  }, [navigate]);

  const handleGenerate = async () => {
    if (!content.trim()) { toast.error("Introduce una idea o URL"); return; }
    setLoading(true); setStatusMsg("Iniciando generación..."); setProgress(5);
    try {
      const image_overrides = autoImage ? {
        engine: imgOverrides.engine || undefined,
        style: imgOverrides.style || undefined,
        secondary_style: imgOverrides.secondary_style || undefined,
        mood: imgOverrides.mood || undefined,
        custom_prompt: imgOverrides.custom_prompt?.slice(0, 200) || undefined,
      } : undefined;
      const res = await articlesApi.generate({
        input_type: inputType,
        content: content.trim(),
        topic_hint: topicHint.trim() || null,
        language: hasProAccess ? language : "es",
        networks: selectedNetworks.length > 0 ? selectedNetworks : null,
        auto_image: autoImage,
        image_overrides,
      });
      if (res.data.job_id) { pollStatus(res.data.job_id); }
      else if (res.data.id) { toast.success("Artículo generado"); navigate(`/articles/${res.data.id}`); }
    } catch (err) { setLoading(false); setStatusMsg(""); toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleNetwork = (id) => {
    setSelectedNetworks((prev) => prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]);
  };

  return (
    <div data-testid="new-article-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(0,212,255,0.08)" }}>
            <Sparkles className="w-7 h-7" style={{ color: "#00D4FF" }} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>Nuevo Artículo</h1>
          <p className="text-sm" style={{ color: "#94A3B8" }}>Introduce una idea o URL y deja que la IA forje tu artículo</p>
        </div>

        <div className="rounded-xl border p-4 sm:p-6 animate-fade-in-up animate-fade-in-up-delay-1" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>

          {/* Voice Profile (locked for free/basic) */}
          <VoiceProfileSummary hasAccess={hasProAccess} />

          {/* Angle & Objective (locked for free/basic) */}
          <AngleObjectiveSection hasAccess={hasProAccess} profile={voiceProfile} />

          {/* Language Selector */}
          <div className="relative rounded-xl border p-4 mb-5"
            style={{ background: "#0F2035", borderColor: hasProAccess ? "rgba(0,212,255,0.15)" : "rgba(251,191,36,0.15)" }}>
            {!hasProAccess && <LockedOverlay planName="Pro" />}
            <div className={!hasProAccess ? "opacity-40" : ""}>
              <div className="flex items-center gap-2 mb-3">
                <Globe2 className="w-4 h-4" style={{ color: "#00D4FF" }} />
                <span className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Idioma del contenido</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map((lang) => (
                  <button key={lang.id} data-testid={`lang-${lang.id}`}
                    onClick={() => hasProAccess && setLanguage(lang.id)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs transition-all"
                    style={{
                      background: language === lang.id ? "rgba(0,212,255,0.08)" : "transparent",
                      borderColor: language === lang.id ? "rgba(0,212,255,0.35)" : "#1E3A5F",
                      color: language === lang.id ? "#00D4FF" : "#94A3B8",
                    }}>
                    <span className="font-bold text-xs" style={{ fontFamily: "'IBM Plex Mono'" }}>{lang.flag}</span>
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Base Form (accessible to all) */}
          <Tabs value={inputType} onValueChange={setInputType}>
            <TabsList data-testid="input-type-tabs" className="w-full grid grid-cols-2 mb-4 h-10" style={{ background: "#0B1622" }}>
              <TabsTrigger data-testid="tab-idea" value="idea" className="text-sm data-[state=active]:text-white" style={{ fontFamily: "'Outfit'" }}>
                <Lightbulb className="w-4 h-4 mr-1.5" />Idea
              </TabsTrigger>
              <TabsTrigger data-testid="tab-url" value="url" className="text-sm data-[state=active]:text-white" style={{ fontFamily: "'Outfit'" }}>
                <Link2 className="w-4 h-4 mr-1.5" />URL
              </TabsTrigger>
            </TabsList>
            <TabsContent value="idea" className="space-y-4">
              <div>
                <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Describe tu idea</Label>
                <Textarea data-testid="idea-textarea" value={content} onChange={(e) => setContent(e.target.value)}
                  placeholder="Ej: Los beneficios de la meditación para emprendedores tech..." rows={5} className="resize-none text-sm"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
              </div>
            </TabsContent>
            <TabsContent value="url" className="space-y-4">
              <div>
                <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>URL del contenido</Label>
                <Input data-testid="url-input" value={content} onChange={(e) => setContent(e.target.value)}
                  placeholder="https://ejemplo.com/articulo" className="h-11 text-sm"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-4">
            <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Enfoque específico (opcional)</Label>
            <Input data-testid="topic-hint-input" value={topicHint} onChange={(e) => setTopicHint(e.target.value)}
              placeholder="Ej: Enfocado en pequeñas empresas..." className="h-11 text-sm"
              style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
          </div>

          {/* Redes Sociales — opcional */}
          <div className="mt-5 rounded-xl border p-4" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
            <div className="flex items-center gap-2 mb-3">
              <Share2 className="w-4 h-4" style={{ color: "#00D4FF" }} />
              <span className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Redes sociales (opcional)</span>
              <span className="text-xs ml-auto" style={{ color: "#64748B" }}>Se generarán en paralelo</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {NETWORKS.map((net) => {
                const active = selectedNetworks.includes(net.id);
                return (
                  <button
                    key={net.id}
                    type="button"
                    data-testid={`network-checkbox-${net.id}`}
                    onClick={() => toggleNetwork(net.id)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs transition-all"
                    style={{
                      background: active ? "rgba(0,212,255,0.08)" : "transparent",
                      borderColor: active ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                      color: active ? "#00D4FF" : "#94A3B8",
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded flex items-center justify-center border flex-shrink-0"
                      style={{
                        background: active ? "#00D4FF" : "transparent",
                        borderColor: active ? "#00D4FF" : "#334155",
                      }}
                    >
                      {active && <Check className="w-3 h-3" style={{ color: "#0B1622" }} />}
                    </div>
                    <span className="truncate">{net.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-2" style={{ color: "#64748B" }}>
              Si no seleccionas ninguna, solo se genera el artículo (puedes crear redes después desde el detalle).
            </p>
          </div>

          {/* Imagen destacada — opcional, con controles per-artículo */}
          <div className="mt-3 rounded-xl border" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
            <div className="flex items-center gap-2 p-3">
              <div
                data-testid="auto-image-checkbox"
                onClick={() => setAutoImage((v) => !v)}
                className="w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 cursor-pointer"
                style={{
                  background: autoImage ? "#00D4FF" : "transparent",
                  borderColor: autoImage ? "#00D4FF" : "#334155",
                }}
              >
                {autoImage && <Check className="w-3 h-3" style={{ color: "#0B1622" }} />}
              </div>
              <ImageIcon className="w-3.5 h-3.5" style={{ color: autoImage ? "#00D4FF" : "#64748B" }} />
              <span className="text-xs flex-1" style={{ color: autoImage ? "#E2E8F0" : "#94A3B8" }}>
                Imagen destacada automática
              </span>
              {autoImage && (
                <button
                  type="button"
                  data-testid="img-section-toggle"
                  onClick={() => setImgSectionOpen((v) => !v)}
                  className="text-xs flex items-center gap-1 transition-all"
                  style={{ color: "#00D4FF" }}
                >
                  {imgSectionOpen ? "Ocultar opciones" : "Opciones de estilo"}
                  <ChevronDown className={`w-3 h-3 transition-transform ${imgSectionOpen ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>
            {autoImage && imgSectionOpen && (
              <div className="px-4 pb-4 pt-1 space-y-3 animate-fade-in-up" style={{ borderTop: "1px solid #1E3A5F" }}>
                <div className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Motor IA */}
                  <div>
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Motor IA</Label>
                    <select
                      data-testid="img-engine-select"
                      value={imgOverrides.engine}
                      onChange={(e) => setImgOverrides({ ...imgOverrides, engine: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg text-xs border"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    >
                      {ENGINES_NEW.map((e) => (<option key={e.id} value={e.id}>{e.label} — {e.desc}</option>))}
                    </select>
                  </div>
                  {/* Estilo principal */}
                  <div>
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Estilo principal</Label>
                    <select
                      data-testid="img-style-select"
                      value={imgOverrides.style}
                      onChange={(e) => setImgOverrides({ ...imgOverrides, style: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg text-xs border"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    >
                      {STYLES.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
                    </select>
                  </div>
                  {/* Estilo secundario */}
                  <div>
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Estilo secundario (opcional)</Label>
                    <select
                      data-testid="img-secondary-select"
                      value={imgOverrides.secondary_style}
                      onChange={(e) => setImgOverrides({ ...imgOverrides, secondary_style: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg text-xs border"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    >
                      <option value="">— Ninguno —</option>
                      {STYLES.filter((s) => s.id !== imgOverrides.style).map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
                    </select>
                  </div>
                  {/* Mood */}
                  <div>
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Ambiente (Mood)</Label>
                    <select
                      data-testid="img-mood-select"
                      value={imgOverrides.mood}
                      onChange={(e) => setImgOverrides({ ...imgOverrides, mood: e.target.value })}
                      className="w-full h-9 px-2 rounded-lg text-xs border"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    >
                      <option value="">— Por defecto —</option>
                      {MOODS.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
                    </select>
                  </div>
                </div>
                {/* Prompt libre per-artículo */}
                <div>
                  <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>
                    Descripción adicional (opcional, máx 200 caracteres)
                  </Label>
                  <Textarea
                    data-testid="img-custom-prompt"
                    value={imgOverrides.custom_prompt}
                    onChange={(e) => setImgOverrides({ ...imgOverrides, custom_prompt: e.target.value.slice(0, 200) })}
                    placeholder="Ej: mujer y hombre mirándose en entorno cálido, luz suave, composición central..."
                    maxLength={200}
                    rows={2}
                    className="resize-none text-xs"
                    style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs" style={{ color: "#64748B" }}>
                      Se añade al prompt base tras estilo + paleta de marca
                    </span>
                    <span className="text-xs font-mono" style={{ color: "#64748B" }}>
                      {imgOverrides.custom_prompt.length}/200
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button data-testid="generate-article-btn" onClick={handleGenerate} disabled={loading || !content.trim()}
            className="w-full mt-6 h-12 rounded-full font-medium text-sm transition-all duration-200"
            style={{ background: loading ? "#0369A1" : "linear-gradient(135deg, #00B4D8, #00D4FF)", color: loading ? "#94A3B8" : "#0B1622" }}>
            {loading ? (
              <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /><span>Forjando artículo...</span></div>
            ) : (<><Sparkles className="w-4 h-4 mr-2" />Forjar Artículo<ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>

          {loading && (
            <div className="mt-5 p-5 rounded-xl" style={{ background: "rgba(0,212,255,0.04)", border: "1px solid rgba(0,212,255,0.1)" }}>
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: "#00D4FF" }} />
                <p className="text-sm font-medium" style={{ color: "#E2E8F0" }}>
                  {statusMsg || "Forjando tu artículo..."}
                </p>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "#1E3A5F" }}>
                <div className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progress}%`, background: "linear-gradient(90deg, #00B4D8, #00D4FF)" }} />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs" style={{ color: "#64748B" }}>Esto puede tardar entre 30-90 segundos</span>
                <span className="text-xs font-mono" style={{ color: "#00D4FF" }}>{Math.round(progress)}%</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-in-up animate-fade-in-up-delay-3">
          {[
            { title: "Ideas claras", desc: "Cuanto más específica, mejor el resultado", icon: Lightbulb, color: "#00D4FF" },
            { title: "URLs ricas", desc: "Usa URLs con contenido detallado", icon: Link2, color: "#F0A050" },
            { title: "SEO automático", desc: "Keywords, meta y FAQ incluidos", icon: Sparkles, color: "#4ADE80" },
          ].map((tip) => (
            <div key={tip.title} className="rounded-xl p-4 border card-glow" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <tip.icon className="w-5 h-5 mb-2" style={{ color: tip.color }} />
              <h4 className="text-sm font-medium mb-1" style={{ color: "#E2E8F0", fontFamily: "'Outfit'" }}>{tip.title}</h4>
              <p className="text-xs" style={{ color: "#94A3B8" }}>{tip.desc}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
